import { useState, useEffect } from 'react';
import shopperDataLoader from './shopper-data';
import User from './user';
import Cookies from './cookies';

const status = [
  'partial',
  'full',
  'manager'
];

/**
 * Customer details
 *
 * @Constructor Customer
 * @param {Object} options Optional configuration.
 * @api public
 */
export default class Customer extends User {
  /**
   * Update data from the idp cookie
   *
   * @returns {Customer} fluent interface
   * @api private
   */
  updateFromCookie() {
    this.cookies.updateIdp();
    const auth = this.cookies.getIdpProperty('auth');

    this.set(this.getIdpProperties(this.cookies.getIdpProperties.bind(this.cookies), auth));

    if (!this.shopperId) {
      // Try getting it from the ShopperId cookie instead
      const encryptedShopperId = this.cookies.getEncryptedShopperId(this.options.env, this.options.privateLabelId);
      if (encryptedShopperId) {
        this.set({
          ...this.getAll(),
          shopperId: encryptedShopperId
        });
      }
    }

    return this;
  }

  /**
   * Get properties from idp information
   *
   * @param {Function} getIdpPropertiesFn Function to call to get properties from idp
   * @param {String} auth Type of authentication (e.g. e2s, s2s, e2s2s, none of the above)
   * @returns {Object} Extracted properties
   * @package
   */
  getIdpProperties(getIdpPropertiesFn, auth) {
    return getIdpPropertiesFn([
      'disp',
      'firstname',
      'lastname',
      'info_shopperId',
      'plid',
      'pcx',
      'username'
    ].map(property => [auth, property]).concat('jti'), {
      info_shopperId: 'shopperId',
      disp: 'displayName'
    });
  }

  /**
   * Fetch all user details, unpack and store locally.
   * TODO: JSONP should not be enforced for details but CORS headers are missing.
   *
   * @param {Object} url Request targets.
   * @param {Function} done Completion callback
   * @api public
   */
  fetch(url, done) {
    this.cookies.updateIdp();
    this.fetchInternal(url, done, (data) => {
      let idp;
      try {
        idp = JSON.parse(data.info);
      } catch (e) {
        return {};
      }
      return this.getIdpProperties(Cookies.getRealmPropertiesFromObject.bind(this, idp), idp.auth);
    });
  }

  /**
   * Return loggedIn status of Customer.
   *
   * @returns {Boolean} LoggedIn.
   * @api public
   */
  get loggedIn() {
    const privateLabelId = this.options.privateLabelId;

    if (privateLabelId && this.data.plid && privateLabelId.toString() !== this.data.plid.toString()) {
      return false;
    }
    return !!~status.indexOf(this.get('status')) || !!this.get('loginName');
  }
}

/**
 * Get the (logged-in) customer details.
 *
 * @param {String} url API url, usually GUI API.
 * @param {Object} opt Options
 * @param {String} opt.privateLabelId Private Label id.
 * @param {String} [opt.shouldAuthenticate=true] Skip customer details API call if not needed.
 * @returns {Object} customer details
 * @public
 */
export function useCustomerDetails(url, { privateLabelId, shouldAuthenticate = true } = {}) {
  if (!url) return;

  const [customerDetails, setCustomerDetails] = useState({});

  useEffect(() => {
    // Start with data from the existing cookie
    const customer = new Customer().updateFromCookie();

    setCustomerDetails({ customer });

    if (!shouldAuthenticate) {
      shopperDataLoader.configureOnly({
        shopperId: customer.shopperId,
        privateLabelId,
        url
      });

      return;
    }

    // Next, try to fetch fresh data from the server
    customer.fetch({ gui: url }, error => {
      if (error) {
        // We still have some data from the cookie, so just fall back to that.
        // We use the presence of a shopper ID as a weak proxy to login state.
        // This won't actually unlock any real actions -- just tells the header to act
        // like the user is logged in. If they are not really logged in, they'll get
        // sent to SSO when they try to do anything that requires login.
        if (customer.shopperId) {
          return setCustomerDetails({
            displayName: customer.displayName(true),
            federatedPartner: customer.get('federatedPartner'),
            name: customer.displayName(),
            shopperId: customer.shopperId,
            loggedIn: true,
            customer
          });
        }
      }

      const proUser = customer.get('proshopper');
      const proshopper = proUser && proUser.status === 1;
      const rewardPoints = proshopper ? proUser.rewardPoints : null;

      setCustomerDetails({
        displayName: customer.displayName(true),
        federatedPartner: customer.get('federatedPartner'),
        items: customer.cart,
        isPro: proshopper,
        loggedIn: customer.loggedIn,
        name: customer.displayName(),
        proshopper,
        rewardPoints,
        shopperId: customer.shopperId,
        username: customer.username,
        vip: customer.vip,
        pcx: customer.pcx,
        customer
      });
    });
  }, []);

  return customerDetails;
}
